import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getBlogPosts } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { usePageSEO } from '../hooks/usePageSEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { buildWhatsAppUrl } from '../utils/whatsapp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COPY = {
  pt: {
    eyebrow: 'Journal · Your Broker Portugal',
    heroTitle: 'Ensaios sobre',
    heroTitleEm: 'imobiliário e Portugal.',
    heroLead:
      'Pensamento editorial sobre o mercado português — análises, guias e perspectivas escritas para quem decide com profundidade, não com pressa.',
    allLabel: 'Todos',
    searchPlaceholder: 'Procurar por tema, região ou palavra-chave...',
    emptyState: 'Nenhum ensaio corresponde a esta pesquisa.',
    readEssay: 'Ler ensaio',
    closingEyebrow: 'Conversa privada',
    closingTitle: 'A leitura é o início.',
    closingTitleEm: 'A decisão merece uma conversa.',
    closingBody:
      'Os nossos ensaios partilham método e perspectiva. Mas cada projecto é singular — mereça uma análise feita à medida, sem agenda comercial.',
    closingCtaWa: 'Falar via WhatsApp',
    closingCtaContact: 'Iniciar conversa privada',
  },
  en: {
    eyebrow: 'Journal · Your Broker Portugal',
    heroTitle: 'Essays on',
    heroTitleEm: 'real estate and Portugal.',
    heroLead:
      'Editorial thinking on the Portuguese market — analysis, guides and perspectives written for those who decide with depth, not haste.',
    allLabel: 'All',
    searchPlaceholder: 'Search by topic, region or keyword...',
    emptyState: 'No essay matches this search.',
    readEssay: 'Read essay',
    closingEyebrow: 'Private conversation',
    closingTitle: 'Reading is the beginning.',
    closingTitleEm: 'The decision deserves a conversation.',
    closingBody:
      'Our essays share method and perspective. Yet every project is singular — it deserves analysis crafted to it, free of commercial agenda.',
    closingCtaWa: 'Chat via WhatsApp',
    closingCtaContact: 'Begin a private conversation',
  },
};

const useReveal = (deps = []) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const nodes = el.querySelectorAll('.rv:not(.is-visible)');
    const vh = window.innerHeight || 0;
    nodes.forEach((n) => {
      const r = n.getBoundingClientRect();
      if (r.top < vh && r.bottom > 0) n.classList.add('is-visible');
    });
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible');
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.1, rootMargin: '0px 0px -10% 0px' }
    );
    el.querySelectorAll('.rv:not(.is-visible)').forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, deps);
  return ref;
};

const Eyebrow = ({ children, dark = false }) => (
  <p
    className={`inline-flex items-center gap-3 text-[11px] font-medium uppercase ${
      dark ? 'text-editorial-bronze-lt' : 'text-editorial-bronze'
    }`}
    style={{ letterSpacing: '0.24em', fontFamily: 'DM Sans, sans-serif' }}
  >
    <span className={`block w-7 h-px ${dark ? 'bg-editorial-bronze-lt' : 'bg-editorial-bronze'}`} />
    {children}
  </p>
);

const resolveImage = (img) => {
  if (!img) return null;
  if (img.startsWith('http')) return img;
  return `${BACKEND_URL}${img}`;
};

const BlogPage = () => {
  const { language, localizedPath } = useLanguage();
  const copy = COPY[language] || COPY.pt;
  const { title: seoTitle, description: seoDescription } = usePageSEO('blog', language);
  const [homeContent, setHomeContent] = useState(null);
  const [posts, setPosts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  const getTranslated = (obj, field) => {
    if (!obj) return '';
    if (language === 'en' && obj[`${field}_en`]) return obj[`${field}_en`];
    return obj[field] || '';
  };

  useEffect(() => {
    Promise.all([
      axios.get(`${BACKEND_URL}/api/home-content`).catch(() => null),
      getBlogPosts().catch(() => []),
    ]).then(([home, blogPosts]) => {
      setHomeContent(home?.data || null);
      setPosts(blogPosts || []);
      setLoading(false);
    });
  }, []);

  const ref = useReveal([posts.length, searchTerm, activeCategory]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return d.toLocaleDateString(language === 'en' ? 'en-US' : 'pt-PT', {
      year: 'numeric',
      month: 'long',
    });
  };

  const categories = Array.from(
    new Set(posts.map((p) => getTranslated(p, 'category')).filter(Boolean))
  );

  const filtered = posts.filter((post) => {
    const term = searchTerm.trim().toLowerCase();
    const matchesTerm =
      !term ||
      getTranslated(post, 'title').toLowerCase().includes(term) ||
      getTranslated(post, 'excerpt').toLowerCase().includes(term) ||
      getTranslated(post, 'category').toLowerCase().includes(term);
    const matchesCategory =
      activeCategory === 'all' ||
      getTranslated(post, 'category').toLowerCase() === activeCategory.toLowerCase();
    return matchesTerm && matchesCategory;
  });

  return (
    <>
      <SEO title={seoTitle} description={seoDescription} url="https://yourbrokerportugal.com/journal" />
      <style>{`
        body { background: #f7f4ef; }
        @keyframes rvFadeUp { from { opacity: 0; transform: translateY(28px); } to { opacity: 1; transform: translateY(0); } }
        .rv { animation: rvFadeUp .95s ease both; }
        .journal-search::placeholder { color: rgba(28,28,26,0.35); font-style: italic; }
        .journal-search:focus { outline: none; border-bottom-color: #b8955a; }
      `}</style>

      <Header
        companyName={homeContent?.headerFooter?.companyName}
        logo={homeContent?.headerFooter?.logo}
      />

      <main ref={ref}>
        {/* HERO */}
        <section className="pt-28 md:pt-36 pb-16 md:pb-20 px-6 md:px-10 lg:px-16 xl:px-24 bg-editorial-mineral">
          <div className="max-w-5xl mx-auto">
            <div className="rv">
              <Eyebrow>{copy.eyebrow}</Eyebrow>
            </div>
            <h1
              className="rv text-editorial-charcoal font-light mt-7 mb-8 max-w-4xl"
              style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: 'clamp(2.4rem, 4.4vw, 4rem)',
                lineHeight: '1.08',
              }}
              data-testid="journal-hero-title"
            >
              {copy.heroTitle}{' '}
              <em className="italic text-editorial-bronze">{copy.heroTitleEm}</em>
            </h1>
            <p
              className="rv text-editorial-muted font-light max-w-2xl"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
            >
              {copy.heroLead}
            </p>
          </div>
        </section>

        {/* FILTERS — minimal editorial bar */}
        <section className="px-6 md:px-10 lg:px-16 xl:px-24 bg-editorial-mineral border-t border-editorial-sand-deep/30">
          <div className="max-w-6xl mx-auto py-7 md:py-9">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center">
              {/* Search */}
              <div className="md:col-span-5">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={copy.searchPlaceholder}
                  className="journal-search w-full bg-transparent border-0 border-b border-editorial-sand-deep/60 py-2 text-editorial-charcoal placeholder:text-editorial-muted/50"
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '0.92rem',
                    borderRadius: 0,
                  }}
                  data-testid="journal-search-input"
                />
              </div>

              {/* Categories */}
              <div className="md:col-span-7 flex flex-wrap gap-x-7 gap-y-2 md:justify-end">
                <button
                  onClick={() => setActiveCategory('all')}
                  className={`pb-0.5 border-b transition-colors ${
                    activeCategory === 'all'
                      ? 'text-editorial-charcoal border-editorial-bronze'
                      : 'text-editorial-muted/70 border-transparent hover:text-editorial-charcoal hover:border-editorial-sand-deep'
                  }`}
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '0.65rem',
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    fontWeight: 500,
                  }}
                  data-testid="journal-filter-all"
                >
                  {copy.allLabel}
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`pb-0.5 border-b transition-colors ${
                      activeCategory === cat
                        ? 'text-editorial-charcoal border-editorial-bronze'
                        : 'text-editorial-muted/70 border-transparent hover:text-editorial-charcoal hover:border-editorial-sand-deep'
                    }`}
                    style={{
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: '0.65rem',
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      fontWeight: 500,
                    }}
                    data-testid={`journal-filter-${cat}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* POSTS GRID */}
        <section className="px-6 md:px-10 lg:px-16 xl:px-24 py-16 md:py-24 lg:py-28 bg-editorial-sand/30">
          <div className="max-w-6xl mx-auto">
            {loading ? (
              <p
                className="text-editorial-muted text-center"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem' }}
              >
                {language === 'pt' ? 'A carregar...' : 'Loading...'}
              </p>
            ) : filtered.length === 0 ? (
              <p
                className="text-editorial-muted italic text-center max-w-md mx-auto"
                style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  fontSize: '1.35rem',
                  lineHeight: '1.55',
                }}
              >
                {copy.emptyState}
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-16 md:gap-y-20">
                {filtered.map((post) => {
                  const slug = post.slug || post.id;
                  const title = getTranslated(post, 'title');
                  const excerpt = getTranslated(post, 'excerpt');
                  const category = getTranslated(post, 'category');
                  const img = resolveImage(post.image);
                  return (
                    <Link
                      key={post.id || slug}
                      to={localizedPath(`/journal/${slug}`)}
                      className="rv group block"
                      data-testid={`journal-card-${slug}`}
                    >
                      <div className="aspect-[4/5] overflow-hidden bg-editorial-sand mb-7">
                        {img ? (
                          <img
                            src={img}
                            alt={title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            style={{ filter: 'contrast(1.02)' }}
                          />
                        ) : (
                          <div className="w-full h-full bg-editorial-sand" />
                        )}
                      </div>

                      {category && (
                        <p
                          className="text-editorial-bronze mb-3"
                          style={{
                            fontFamily: 'DM Sans, sans-serif',
                            fontSize: '0.6rem',
                            letterSpacing: '0.18em',
                            textTransform: 'uppercase',
                            fontWeight: 500,
                          }}
                        >
                          {category}
                          {post.created_at && (
                            <span className="text-editorial-muted/60 ml-3 normal-case tracking-normal">
                              · {formatDate(post.created_at)}
                            </span>
                          )}
                        </p>
                      )}

                      <h2
                        className="text-editorial-charcoal font-light mb-4 group-hover:text-editorial-bronze transition-colors"
                        style={{
                          fontFamily: 'Cormorant Garamond, serif',
                          fontSize: 'clamp(1.4rem, 1.8vw, 1.75rem)',
                          lineHeight: '1.22',
                        }}
                      >
                        {title}
                      </h2>

                      {excerpt && (
                        <p
                          className="text-editorial-muted font-light line-clamp-3 mb-5"
                          style={{
                            fontFamily: 'DM Sans, sans-serif',
                            fontSize: '0.92rem',
                            lineHeight: '1.92',
                          }}
                        >
                          {excerpt}
                        </p>
                      )}

                      <span
                        className="inline-block text-editorial-charcoal border-b border-editorial-sand-deep group-hover:border-editorial-bronze group-hover:text-editorial-bronze pb-0.5 transition-colors"
                        style={{
                          fontFamily: 'DM Sans, sans-serif',
                          fontSize: '0.6rem',
                          letterSpacing: '0.18em',
                          textTransform: 'uppercase',
                          fontWeight: 500,
                        }}
                      >
                        {copy.readEssay}
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* CLOSING */}
        <section className="px-6 md:px-10 lg:px-16 xl:px-24 py-24 md:py-32 lg:py-40 bg-editorial-charcoal text-editorial-bone">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
            <div className="rv">
              <Eyebrow dark>{copy.closingEyebrow}</Eyebrow>
              <h2
                className="text-editorial-bone font-light mt-7 mb-7"
                style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  fontSize: 'clamp(2.2rem, 4vw, 3.6rem)',
                  lineHeight: '1.08',
                }}
              >
                {copy.closingTitle}{' '}
                <em className="italic text-editorial-bronze-lt">{copy.closingTitleEm}</em>
              </h2>
              <p
                className="text-editorial-bone/60 font-light max-w-lg"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.95rem', lineHeight: '1.95' }}
              >
                {copy.closingBody}
              </p>
            </div>
            <div className="rv flex flex-col gap-5 items-start">
              <a
                href={buildWhatsAppUrl('journal', language)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-editorial-charcoal bg-editorial-sand hover:bg-editorial-bone transition-all duration-300 hover:-translate-y-0.5 py-4 px-10"
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '0.65rem',
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  fontWeight: 500,
                  borderRadius: 0,
                }}
                data-testid="journal-closing-cta-whatsapp"
              >
                {copy.closingCtaWa}
              </a>
              <Link
                to={localizedPath('/contact')}
                className="text-editorial-bone/60 hover:text-editorial-bone border-b border-editorial-bone/22 hover:border-editorial-bone pb-0.5 transition-colors"
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '0.65rem',
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  fontWeight: 500,
                }}
                data-testid="journal-closing-cta-contact"
              >
                {copy.closingCtaContact}
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer data={homeContent?.headerFooter} />
    </>
  );
};

export default BlogPage;
